import { useState } from "react"

import { Button, Checkbox, Input, Tooltip } from "@coval/ui"
import { Check, Pencil, Plus, X } from "lucide-react"

import { generateShortUUID } from "@/utils/id-generation/short-uuid"

import type { CustomDimension } from "../utils/reportViewConfig"

interface SelectionBreakdownEntry {
  groupId: string | null
  label: string
  color?: string
  selectedCount: number
}

interface CustomDimensionSelectionPanelProps {
  dimension: CustomDimension
  onChange: (next: CustomDimension) => void
  onDone: () => void
  onDelete: () => void
  groupColorById?: Record<string, string>
  /** Total simulations currently selected in the right-pane table. */
  totalSelectedCount: number
  /** Per-bucket breakdown of the current selection — one entry per group plus an "Unassigned" entry. Filtered to non-zero counts at render time. */
  selectionBreakdown: SelectionBreakdownEntry[]
}

export function CustomDimensionSelectionPanel({
  dimension,
  onChange,
  onDone,
  onDelete,
  groupColorById,
  totalSelectedCount,
  selectionBreakdown,
}: CustomDimensionSelectionPanelProps) {
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState("")
  const [dimensionNameDraft, setDimensionNameDraft] = useState(dimension.name)
  const [isEditingDimensionName, setIsEditingDimensionName] = useState(false)

  const commitDimensionName = () => {
    const trimmed = dimensionNameDraft.trim()
    if (trimmed.length === 0) {
      setDimensionNameDraft(dimension.name)
      setIsEditingDimensionName(false)
      return
    }
    if (trimmed !== dimension.name) {
      onChange({ ...dimension, name: trimmed })
    }
    setIsEditingDimensionName(false)
  }

  const startRenameGroup = (groupId: string, current: string) => {
    setRenamingGroupId(groupId)
    setRenameDraft(current)
  }

  const commitRenameGroup = () => {
    if (!renamingGroupId) {
      return
    }
    const trimmed = renameDraft.trim()
    if (trimmed.length === 0) {
      setRenamingGroupId(null)
      return
    }
    onChange({
      ...dimension,
      groups: dimension.groups.map((g) =>
        g.id === renamingGroupId ? { ...g, name: trimmed } : g,
      ),
    })
    setRenamingGroupId(null)
  }

  const handleAddGroup = () => {
    const id = generateShortUUID()
    const nextIndex = dimension.groups.length + 1
    const newGroup = { id, name: `Group ${nextIndex}`, simulationIds: [] }
    onChange({ ...dimension, groups: [...dimension.groups, newGroup] })
    startRenameGroup(id, newGroup.name)
  }

  const handleDeleteGroup = (groupId: string) => {
    onChange({
      ...dimension,
      groups: dimension.groups.filter((g) => g.id !== groupId),
    })
  }

  const handleToggleHide = (checked: boolean | "indeterminate") => {
    onChange({ ...dimension, hideUnassigned: checked === true })
  }

  const visibleBreakdown = selectionBreakdown.filter(
    (entry) => entry.selectedCount > 0,
  )

  return (
    <div className="h-full flex flex-col bg-surface-0">
      <div className="shrink-0 px-4 py-3 border-b border-coval-border flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-coval-text-muted font-semibold">
            Editing dimension
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onDelete}>
              Delete
            </Button>
            <Button onClick={onDone}>
              <Check />
              Done
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditingDimensionName ? (
            <Input
              autoFocus
              aria-label="Dimension name"
              value={dimensionNameDraft}
              onChange={(e) => setDimensionNameDraft(e.target.value)}
              onBlur={commitDimensionName}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitDimensionName()
                } else if (e.key === "Escape") {
                  setDimensionNameDraft(dimension.name)
                  setIsEditingDimensionName(false)
                }
              }}
              className="text-base"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setDimensionNameDraft(dimension.name)
                setIsEditingDimensionName(true)
              }}
              className="text-base font-medium text-coval-text hover:underline text-left"
            >
              {dimension.name}
            </button>
          )}
          {!isEditingDimensionName && (
            <Tooltip content="Rename dimension">
              <button
                type="button"
                aria-label="Rename dimension"
                className="text-coval-text-muted hover:text-coval-text"
                onClick={() => {
                  setDimensionNameDraft(dimension.name)
                  setIsEditingDimensionName(true)
                }}
              >
                <Pencil className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="shrink-0 border-b border-coval-border px-4 py-3 flex flex-col gap-2">
        <span className="text-[10px] uppercase tracking-wider text-coval-text-muted font-semibold">
          Selection
        </span>
        {totalSelectedCount === 0 ? (
          <p className="text-xs text-coval-text-muted">
            Check simulations in the table, then click{" "}
            <span className="font-medium">Assign to…</span> to add them to a
            group.
          </p>
        ) : (
          <>
            <div className="text-sm text-coval-text">
              {totalSelectedCount} selected
            </div>
            <div className="flex flex-col gap-1">
              {visibleBreakdown.map((entry) => (
                <div
                  key={entry.groupId ?? "__unassigned__"}
                  className="flex items-center gap-2 text-xs"
                >
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
                    style={{
                      backgroundColor: entry.color ?? "var(--coval-text-muted)",
                    }}
                  />
                  <span className="flex-1 truncate text-coval-text">
                    {entry.label}
                  </span>
                  <span className="text-coval-text-muted">
                    {entry.selectedCount}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-4 py-3 flex flex-col gap-2">
        <span className="text-[10px] uppercase tracking-wider text-coval-text-muted font-semibold">
          Groups
        </span>
        {dimension.groups.length === 0 && (
          <div className="text-xs text-coval-text-muted py-2">
            No groups yet. Click <span className="font-medium">+ Group</span> to
            start, or use{" "}
            <span className="font-medium">+ New group from selection</span> in
            the assign menu.
          </div>
        )}
        {dimension.groups.map((group) => {
          const isRenaming = renamingGroupId === group.id
          return (
            <div
              key={group.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded border border-coval-border bg-surface-0"
            >
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 rounded-sm shrink-0"
                style={{
                  backgroundColor:
                    groupColorById?.[group.id] ?? "var(--coval-text-muted)",
                }}
              />
              {isRenaming ? (
                <Input
                  autoFocus
                  aria-label="Rename group"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onBlur={commitRenameGroup}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      commitRenameGroup()
                    } else if (e.key === "Escape") {
                      setRenamingGroupId(null)
                    }
                  }}
                  className="flex-1 h-7 text-sm"
                />
              ) : (
                <button
                  type="button"
                  className="flex-1 text-left text-sm font-medium text-coval-text hover:underline"
                  onClick={() => startRenameGroup(group.id, group.name)}
                  aria-label={`Rename ${group.name}`}
                >
                  {group.name}
                </button>
              )}
              <span className="text-xs text-coval-text-muted">
                {group.simulationIds.length}
              </span>
              {!isRenaming && (
                <Tooltip content="Rename">
                  <button
                    type="button"
                    aria-label={`Rename ${group.name}`}
                    className="text-coval-text-muted hover:text-coval-text"
                    onClick={() => startRenameGroup(group.id, group.name)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </Tooltip>
              )}
              <Tooltip content="Delete group">
                <button
                  type="button"
                  aria-label={`Delete ${group.name}`}
                  className="text-coval-text-muted hover:text-coval-text"
                  onClick={() => handleDeleteGroup(group.id)}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
          )
        })}
        <Button variant="secondary" onClick={handleAddGroup}>
          <Plus />
          Group
        </Button>
      </div>

      <div className="shrink-0 border-t border-coval-border px-4 py-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-coval-text-muted">
          <Checkbox
            aria-label="Hide unassigned simulated conversations"
            checked={dimension.hideUnassigned}
            onCheckedChange={handleToggleHide}
          />
          Hide unassigned when grouped
        </label>
      </div>
    </div>
  )
}
